import React, { useState, useEffect } from "react";
import { Tooltip } from "@mui/material";
import { useDispatch } from "react-redux";
import { MdDelete } from "react-icons/md";
import { useHistory } from "react-router";
import { IconButton } from "@material-ui/core";
import { Card, Container } from "reactstrap";
import { TfiEye, TfiPencilAlt } from "react-icons/tfi";

import { ReactComponent as BackIcon } from "assets/img/svg/BackButton.svg";
import { ReactComponent as ActiveControl } from "assets/img/svg/ActiveControl.svg";
import { ReactComponent as InactiveControl } from "assets/img/svg/InactiveControl.svg";

import APopover from "views/pages/manage/common/a-popover";
import AButton from "views/pages/manage/common/form-fields-mui/AButton";
import ADataGrid from "views/pages/manage/common/data-grid/EnhanceDataGrid";
import { AGridColDef } from "views/pages/manage/common/data-grid/ADataGridColDef";
import { setLangAttributeMasterDropdowns } from "redux/globalData/globalDataAction";
import { LastUpdatedDetails } from "views/pages/manage/employeeManagement/userProfile/helper";
import { successToast } from "views/pages/manage/common/utils/methods/toasterFunctions/function";
import { failureToast } from "views/pages/manage/common/utils/methods/toasterFunctions/function";
import SelectMaterialElasticSearch from "../onBoardingMaterialIdSetup/SelectMaterialElasticSearch";

import {
  deleteCourseMaterialEntry,
  editCourseMaterialMapping,
  extractAndJoinMaterialIds,
  extractPkgCode,
  joinCourseIds,
  searchCourseMaterialMapping,
} from "./utilsFunctions";
import { DEFAULT_FILTER_STATE } from "./helper";
import Filters from "./Filters";
import ViewRowDetails from "./ViewRowDetails";
import EditDeleteConfirmationDialog from "./EditDeleteConfirmationDialog";

const CourseMaterialListing = () => {
  const history = useHistory();
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(false);

  const [filters, setFilters] = useState({
    searchBy: "",
    searchByValue: "",
    ...DEFAULT_FILTER_STATE,
  });
  const [filterCount, setFilterCount] = useState(0);

  const [rowData, setRowData] = useState([]);
  const [pageSize, setPageSize] = useState(50);
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);

  const [isConfirmationOpen, setIsConfirmationOpen] = useState({
    open: false,
    editedData: null,
    type: "", // 'edit'/ 'delete'
  });
  const [rowDetailsDialog, setRowDetailsDialog] = useState({
    isOpen: false,
    rowData: null,
  });

  useEffect(() => {
    fetchList({});

    dispatch(setLangAttributeMasterDropdowns());
  }, []);

  const fetchList = async ({
    request = filters,
    page = 0,
    limit = pageSize,
  }) => {
    setIsLoading(true);

    const apiResponse = await searchCourseMaterialMapping({
      request,
      page,
      limit,
    });

    const { totalRecords = 0, response = [] } = apiResponse || {};
    setRowData(response);
    setTotalRows(totalRecords);

    setIsLoading(false);
  };

  const statusHandler = (data, updatedStatus) => {
    setIsConfirmationOpen({
      open: true,
      editedData: {
        ...data,
        updatedStatus,
      },
      type: "edit",
    });
  };

  const editStatus = async () => {
    const updatedRowId = isConfirmationOpen.editedData.id;
    const response = await editCourseMaterialMapping({
      id: updatedRowId,
      payload: { status: isConfirmationOpen.editedData.updatedStatus },
    });

    closeConfirmationDialog();

    if (response.code !== 200) {
      failureToast("Unable to update the status. Please try again.");
      return;
    }

    successToast("Updated successfully");
    const updatedRows = rowData.map((item) =>
      item.id === updatedRowId ? response.data : item
    );
    setRowData(updatedRows);
  };

  const deleteMapping = async () => {
    const deletedRowId = isConfirmationOpen.editedData.id;

    const apiResponse = await deleteCourseMaterialEntry(deletedRowId);

    closeConfirmationDialog();

    if (apiResponse.code !== 200) {
      failureToast("Unable to delete the entry. Please try again.");
      return;
    }

    successToast("Deleted successfully");
    const updatedRows = rowData.filter((item) => item.id !== deletedRowId);
    setRowData(updatedRows);
  };

  const closeConfirmationDialog = () => {
    setIsConfirmationOpen({
      open: false,
      editedData: null,
      type: "",
    });
  };

  const closeRowDetailsDialog = () => {
    setRowDetailsDialog({ isOpen: false, rowData: null });
  };

  const applyFilters = (f) => {
    if (filters.searchBy === "courseId") {
      delete filters.searchBy;
      delete filters.searchByValue;
    }

    const updatedFilters = {
      ...filters,
      ...f,
      ...(f?.courseId && {
        searchBy: "courseId",
        searchByValue: f.courseId.value,
      }),
    };

    delete updatedFilters.courseId;
    setFilters(updatedFilters);

    fetchList({ request: updatedFilters, page: 0 });
  };

  const searchInputHandler = (value) => {
    const updatedFilters = {
      ...filters,
      searchBy: value ? "materialId" : "",
      searchByValue: value,
    };
    setFilters(updatedFilters);

    fetchList({
      request: updatedFilters,
      page: 0,
    });
  };

  const nextButtonHandler = (pageNum, updatedPageSize) => {
    if (pageSize !== updatedPageSize) {
      setPageSize(updatedPageSize);
      setCurrentPage(0);
    } else setCurrentPage(pageNum);

    fetchList({ page: pageNum, limit: updatedPageSize });
  };

  const columns = [
    new AGridColDef("academicCareer", "CAREER")
      .setMinWidth(30)
      .renderCellComponent((params) => (
        <div className="ellipsis-text-container">{params?.value}</div>
      )),

    new AGridColDef("language", "LANGUAGE")
      .setMinWidth(30)
      .renderCellComponent((params) => (
        <div className="ellipsis-text-container">{params?.value || "-"}</div>
      )),

    new AGridColDef("classes", "CLASS")
      .setMinWidth(30)
      .renderCellComponent((params) => (
        <div className="ellipsis-text-container">{params?.value || "-"}</div>
      )),

    new AGridColDef("attribute", "ATTRIBUTE")
      .setMinWidth(50)
      .renderCellComponent((params) => (
        <div className="ellipsis-text-container">{params?.value}</div>
      )),

    new AGridColDef("courseIds", "COURSE ID")
      .setMinWidth(140)
      .renderCellComponent((params) => (
        <Tooltip title={joinCourseIds(params?.value)}>
          <div className="ellipsis-text-container">
            {joinCourseIds(params?.value)}
          </div>
        </Tooltip>
      )),

    new AGridColDef("year", "YEAR")
      .setMinWidth(50)
      .renderCellComponent((params) => (
        <div className="ellipsis-text-container">{params?.value || "-"}</div>
      )),

    new AGridColDef("packageCodeId", "PKG. CODE")
      .setMinWidth(100)
      .renderCellComponent((params) => (
        <Tooltip title={extractPkgCode(params.row.materialInfo)}>
          <div className="ellipsis-text-container">
            {extractPkgCode(params.row.materialInfo)}
          </div>
        </Tooltip>
      )),

    new AGridColDef("materialInfo", "MATERIAL ID")
      .setMinWidth(200)
      .renderCellComponent((params) => (
        <Tooltip title={extractAndJoinMaterialIds(params.value)}>
          <div className="ellipsis-text-container">
            {extractAndJoinMaterialIds(params.value)}
          </div>
        </Tooltip>
      )),

    new AGridColDef("updatedOn", "LAST UPDATED")
      .setMinWidth(100)
      .renderCellComponent((params) => (
        <Tooltip
          title={LastUpdatedDetails(params?.value, params?.row.updatedBy)}
        >
          <div className="ellipsis-text-container">
            {LastUpdatedDetails(params?.value, params?.row.updatedBy)}
          </div>
        </Tooltip>
      )),

    new AGridColDef("status", "STATUS")
      .columnAlign("center")
      .renderCellComponent((params) => (
        <div>
          {params?.value?.toUpperCase() === "ACTIVE" ? (
            <ActiveControl
              onClick={() => statusHandler(params.row, "INACTIVE")}
            />
          ) : (
            <InactiveControl
              onClick={() => statusHandler(params.row, "ACTIVE")}
            />
          )}
          <span className="ml-2 text-xs">{params.value}</span>
        </div>
      )),

    new AGridColDef("action", "Action", false)
      .setMinWidth(100)
      .pinColumnLast(true)
      .renderCellComponent((params) => (
        <div>
          <Tooltip title="View">
            <IconButton
              className="p-0"
              onClick={() => {
                setRowDetailsDialog({ isOpen: true, rowData: params.row });
              }}
            >
              <TfiEye color="#00B0F5" size="18px" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Edit">
            <IconButton
              className="p-0"
              onClick={() => {
                history.push("/admin/edp/courseMaterialSetup/" + params.row.id);
              }}
            >
              <TfiPencilAlt color="#00B0F5" size="16px" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Delete">
            <IconButton
              className="p-0"
              onClick={() => {
                setIsConfirmationOpen({
                  open: true,
                  editedData: params.row,
                  type: "delete",
                });
              }}
            >
              <MdDelete color="red" size="18px" />
            </IconButton>
          </Tooltip>
        </div>
      )),
  ];

  return (
    <>
      <Container className="mt-3" fluid>
        <Card>
          <div className="border-0 p-4">
            <div className="w-100 d-flex justify-content-between align-items-center">
              <div
                className={`d-flex align-items-center`}
                style={{ height: "min-content" }}
              >
                <BackIcon
                  className="mr-2 large-text"
                  onClick={() => history.goBack()}
                />
                <div className="heading-3 color-black-60 text-bold">
                  Course Material ID Setup
                </div>
              </div>

              <div className="d-flex justify-content-center align-items-center">
                <SelectMaterialElasticSearch
                  placeholder={"Search by Material Id or description"}
                  currentValue={
                    filters?.searchBy === "materialId"
                      ? filters.searchByValue
                      : ""
                  }
                  handleChange={(value) => searchInputHandler(value)}
                  isMulti={false}
                  // height={inputHeight}
                  dropdownMappingParams={[
                    "materialIdAndDescription",
                    "materialId",
                    "materialDescription",
                  ]}
                  isDisabled={isLoading}
                  style={{ container: { width: "250px" } }}
                />

                <APopover
                  buttonComponent={(onClick, styles) => (
                    <AButton
                      className="tiny-semi-bold color-dark"
                      disabled={isLoading}
                      onClick={onClick}
                      updatedStyle={{
                        minWidth: "61px",
                        ...(filterCount > 0
                          ? {
                              border: "1px solid #00B0F5",
                              color: "#00B0F5",
                            }
                          : styles),
                      }}
                    >
                      Filters {filterCount > 0 && <>( {filterCount} )</>}
                    </AButton>
                  )}
                  menuComponent={(closeForm) => (
                    <>
                      <Filters
                        closeForm={closeForm}
                        applyFilters={applyFilters}
                        filters={filters}
                        setFilterCount={setFilterCount}
                        filterCount={filterCount}
                      />
                    </>
                  )}
                  // maxHeight={"calc(100vh - 180px)"}
                />

                <AButton
                  size="xs"
                  onClick={() => {
                    history.push("/admin/edp/courseMaterialSetup/new");
                  }}
                  variant="primary_filled"
                  disabled={isLoading}
                >
                  + Add New
                </AButton>
              </div>
            </div>
          </div>

          <div style={{ height: "77vh", maxHeight: "85vh" }}>
            <ADataGrid
              removeWrapperContainer
              rows={rowData || []}
              columns={columns}
              rowId={(row) => row.id}
              rowHeight={50}
              loading={isLoading}
              rowCount={totalRows}
              nextButtonHandler={nextButtonHandler}
              currentPage={currentPage}
              pinLastColumn
              paginationModel={{
                page: currentPage,
                pageSize: pageSize,
              }}
            />
          </div>
        </Card>
      </Container>

      {/* Dialogs */}

      {rowDetailsDialog.isOpen && (
        <ViewRowDetails
          onClose={closeRowDetailsDialog}
          isOpen={rowDetailsDialog.isOpen}
          rowData={rowDetailsDialog.rowData}
        />
      )}

      {isConfirmationOpen.open && (
        <EditDeleteConfirmationDialog
          onClose={closeConfirmationDialog}
          type={isConfirmationOpen.type}
          open={isConfirmationOpen.open}
          data={isConfirmationOpen.editedData}
          editStatus={editStatus}
          deleteMapping={deleteMapping}
        />
      )}
    </>
  );
};

export default CourseMaterialListing;
